import { ChangeDetectorRef, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { SAUTableSetting } from '@some-angular-utils/table';
import { SAUModalService } from '@some-angular-utils/modal';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { DocumentRelation, DocumentRelationChipsComponent } from '@/components/document-relation-chips/document-relation-chips.component';
import { SendToTabletModalComponent } from '@/components/send-to-tablet-modal/send-to-tablet-modal.component';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { Utils } from '@/utils/utils';

/**
 * `signSettings` `icon` values below have to be inline `<svg>` markup, not an `<img src>` -
 * sau-table's `.sau-icon svg { fill: currentColor }` and its `:hover { color: ... }` state only
 * reach a literal `<svg>` element in the DOM; an externally-referenced SVG image is opaque to
 * the host page's CSS regardless of what's inside the file, so it can never pick up the hover
 * recolor. `stroke="currentColor"` (not `fill`) is what actually paints these - they're
 * stroke-drawn line icons (open paths). **`fill="none"` has to sit on each `<path>`, not the
 * root `<svg>`**: that library CSS rule targets the `svg` element directly and, being a real
 * stylesheet rule, overrides a `fill` attribute set on that same `svg` (CSS beats presentation
 * attributes) - it does NOT override a `fill` attribute set on a child `<path>`, since that's a
 * different element the rule never touches. Get this backwards and every open path gets
 * solid-filled (browsers close an open path with a straight line back to its start before
 * filling it), turning e.g. the checkmark into a filled wedge instead of a thin check mark.
 */
const SIGN_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path fill="none" d="M3 15.5c1.3-2.6 2.6-2.6 3.9 0s2.6 2.6 3.9 0 2.6-2.6 3.9 0 2.6-2.6 3.9 0"/><path fill="none" d="M3 19.5h18"/><path fill="none" d="M18.6 15.5l4.4-4.4a1.3 1.3 0 0 0-1.8-1.8l-4.4 4.4"/><path fill="none" d="M20.1 10.6l1.3 1.3" stroke-width="1.3"/></svg>`;
const SIGNED_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path fill="none" d="M4.5 12.75l6 6 9-13.5"/></svg>`;
const SEND_TO_TABLET_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path fill="none" d="M7 3.75h10a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5H7a1.5 1.5 0 0 1-1.5-1.5V5.25A1.5 1.5 0 0 1 7 3.75Z"/><path fill="none" d="M11 18h2" stroke-width="2.5"/><path fill="none" d="M2.5 9.5 5.5 12l-3 2.5" stroke-width="1.6"/></svg>`;

type DocumentSignItem = { id: number; type: string; status: string | null; sign_url?: string | null };

/** Shared `type`/`total`/`relations` sau-table cell templates, plus the "Firmar"/"Ver firmado" row-settings buttons, for the document report list pages. */
@Component({
  selector: 'document-report-cells',
  standalone: true,
  imports: [CentToEurDisplayDirective, DocumentRelationChipsComponent],
  templateUrl: './document-report-cells.component.html',
})
export class DocumentReportCellsComponent {
  @Input() printing = false;
  @Input() docType: string | null = null;

  @ViewChild('typeTmpl', { static: true }) typeTmpl!: TemplateRef<any>;
  @ViewChild('totalTmpl', { static: true }) totalTmpl!: TemplateRef<any>;
  @ViewChild('relationTmpl', { static: true }) relationTmpl!: TemplateRef<any>;

  /** Row-level `sau-table` `[settings]` entries — mutually exclusive `show` predicates (never both true for the same row) reproduce the old "Documento firmado" column's two states: not yet signed vs. already signed, both restricted to saved (non-draft) albaranes. */
  readonly signSettings: SAUTableSetting[] = [
    {
      name: 'Firmar albarán',
      color: '#4f46e5',
      icon: SIGN_ICON,
      show: (item: DocumentSignItem) => item.type === 'albaran' && item.status !== 'draft' && !item.sign_url,
      click: (item: DocumentSignItem) => this.goToSign(item),
    },
    {
      name: 'Ver documento firmado',
      color: '#4f46e5',
      icon: SIGNED_ICON,
      show: (item: DocumentSignItem) => item.type === 'albaran' && item.status !== 'draft' && !!item.sign_url,
      click: (item: DocumentSignItem) => this.viewSignedDocument(item),
    },
    {
      name: 'Enviar a firmar a tablet',
      color: '#4f46e5',
      icon: SEND_TO_TABLET_ICON,
      show: (item: DocumentSignItem) => item.type === 'albaran' && item.status !== 'draft' && !item.sign_url,
      click: (item: DocumentSignItem) => this.openSendToTabletModal(item),
    },
  ];

  private loadingSignIds = new Set<number>();

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private sauModalService: SAUModalService,
  ) { }

  relationLink(rel: DocumentRelation): string {
    return `/invoice/view/${rel.id}`;
  }

  /** True when a factura has at least one albarán pointing at it via document_ref (a "factura de crédito"). */
  hasAlbaranRelation(item: { relations?: DocumentRelation[] }): boolean {
    return !!item.relations?.some(rel => rel.type === 'albaran');
  }

  goToSign(item: { id: number }): void {
    window.open(`/delivery-note-sign/${item.id}`, '_blank');
  }

  openSendToTabletModal(item: { id: number }): void {
    const modalRef = this.sauModalService.open(SendToTabletModalComponent, {});
    modalRef.componentInstance.documentId = item.id;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      modalRef.dismiss();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  isLoadingSign(item: { id: number }): boolean {
    return this.loadingSignIds.has(item.id);
  }

  /** Fetches the signed PDF as an authenticated blob (sign_url is a private-disk path) and opens it in a new tab. */
  viewSignedDocument(item: { id: number }): void {
    if (this.isLoadingSign(item)) return;
    this.loadingSignIds.add(item.id);

    this.apiService.getBlob(`${Urls.urls.documents}/${item.id}/sign`).subscribe({
      next: (blob) => {
        this.loadingSignIds.delete(item.id);
        window.open(URL.createObjectURL(blob), '_blank');
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loadingSignIds.delete(item.id);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'No se pudo obtener el documento firmado');
        this.cdr.detectChanges();
      },
    });
  }
}
